// web/components/login/LoginModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from 'react-google-recaptcha';
import { createPortal } from "react-dom";
import { useAuth } from "../../../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const HAS_RECAPTCHA = !!RECAPTCHA_SITE_KEY && !isLocalhost;

const isAdminEmail = (v = "") => /@promequi\.com\.gt$/i.test(String(v || ""));

export default function LoginModal({ open, onClose }) {
  const { login: setAuthUser } = useAuth();
  const [mode, setMode] = useState("login");
  const dialogRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const recaptchaRef = useRef();

  // States compartidos
  const [loginData, setLoginData] = useState({ user: "", pass: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [resetData, setResetData] = useState({ pass: "", confirm: "" });
  const [signupData, setSignupData] = useState({
    nombre: "",
    empresa: "",
    direccion: "",
    telefono: "",
    correo: "",
    pass: "",
  });

  // Lock scroll y limpieza al cerrar
  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("overflow-y-hidden");
    return () => document.documentElement.classList.remove("overflow-y-hidden");
  }, [open]);

  // Reseteo de estados cuando se cierra
  useEffect(() => {
    if (open) return;
    setMode("login");
    setLoginData({ user: "", pass: "" });
    setForgotEmail("");
    setVerifyCode("");
    setResetData({ pass: "", confirm: "" });
    setSignupData({ nombre: "", empresa: "", direccion: "", telefono: "", correo: "", pass: "" });
    setErr("");
    setLoading(false);
  }, [open]);

  // ESC: cierra sólo si el foco NO está dentro del modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const inside = dialogRef.current?.contains(document.activeElement);
      if (inside) return; // si estás escribiendo, no cerrar
      onClose?.();
    };
    window.addEventListener("keydown", onKey); // sin capture
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Autofocus al abrir
  useEffect(() => {
    if (!open) return;
    const first = dialogRef.current?.querySelector('input,button,select,textarea,[tabindex]:not([tabindex="-1"])');
    first?.focus?.();
  }, [open, mode]);

  if (!open) return null;

  const Section = ({ title, children, onClose: handleClose }) => (
    <div className="bg-white rounded-2xl p-6 sm:p-8 soft border border-slate-100 w-full relative">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-xl font-extrabold" style={{ color: "var(--ink)" }}>
          {title}
        </h3>
        <button
          type="button"
          className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
          onClick={handleClose}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
      {err && (
        <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {err}
        </div>
      )}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );

  // === Handlers ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");

    if (isAdminEmail(loginData.user)) {
      setErr("Cuentas de administración no pueden iniciar sesión desde esta área.");
      return;
    }

    try {
      setLoading(true);
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(`${API_BASE}/api/auth/web/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: loginData.user, pass: loginData.pass, recaptchaToken }),
      });
      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j?.ok) {
        setErr(j?.message || j?.msg || "Credenciales inválidas");
        return;
      }

      setAuthUser(j.user);
      localStorage.setItem("auth_user", JSON.stringify(j.user));
      onClose?.();
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErr("");
      const r = await fetch(`${API_BASE}/api/auth/web/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(signupData),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.msg || "No se pudo crear la cuenta");
      setMode("login");
      setErr("✔ Tu solicitud fue enviada. Te avisaremos por correo.");
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setLoading(false);
    }
  };


  const modalUi = (
    <div className="fixed inset-0 z-[70]">
      {/* Backdrop */}
      <div
        role="presentation"
        className="absolute inset-0 bg-slate-900/60"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      />

      {/* Dialog (no usamos onKeyDownCapture) */}
      <div
        ref={dialogRef}
        className="relative mx-auto max-w-md w-[92%] sm:w-[85%] mt-16 md:mt-24"
        role="dialog"
        aria-modal="true"
      >
        <h2 className="sr-only">Autenticación</h2>
  {HAS_RECAPTCHA && (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />
  )}

        {mode === "login" && (
          <Section title="Inicia sesión" onClose={onClose}>
            <form className="space-y-3" onSubmit={handleLogin}>
              <div>
                <label className="text-sm font-medium text-slate-700">Correo</label>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={loginData.user}
                  onChange={(e) => setLoginData((s) => ({ ...s, user: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Contraseña</label>
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={loginData.pass}
                  onChange={(e) => setLoginData((s) => ({ ...s, pass: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>

              <button type="submit" className="btn btn-brand w-full" disabled={loading}>
                {loading ? "Entrando…" : "Entrar"}
              </button>

              <div className="text-sm flex flex-col gap-2 pt-2">
                <button type="button" className="text-sky-700 underline text-left" onClick={() => setMode("forgot")}>
                  ¿Olvidaste tu contraseña?
                </button>
                <button type="button" className="text-sky-700 underline text-left" onClick={() => setMode("signup")}>
                  Crear usuario
                </button>
              </div>
            </form>
          </Section>
        )}

        {mode === "forgot" && (
          <Section title="Recuperar contraseña" onClose={onClose}>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setMode("verify"); }}>
              <p className="text-sm text-slate-600">Ingresa tu correo y te enviaremos un código de verificación.</p>
              <div>
                <label className="text-sm font-medium text-slate-700">Correo</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn btn-light w-1/3" onClick={() => setMode("login")}>Volver</button>
                <button type="submit" className="btn btn-brand w-2/3">Enviar código</button>
              </div>
            </form>
          </Section>
        )}

        {mode === "verify" && (
          <Section title="Verificar código" onClose={onClose}>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setMode("reset"); }}>
              <p className="text-sm text-slate-600">Ingresa el código que enviamos a <b>{forgotEmail}</b>.</p>
              <div>
                <label className="text-sm font-medium text-slate-700">Código</label>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn btn-light w-1/3" onClick={() => setMode("forgot")}>Volver</button>
                <button type="submit" className="btn btn-brand w-2/3">Validar</button>
              </div>
            </form>
          </Section>
        )}

        {mode === "reset" && (
          <Section title="Nueva contraseña" onClose={onClose}>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setMode("login"); }}>
              <div>
                <label className="text-sm font-medium text-slate-700">Nueva contraseña</label>
                <input
                  type="password"
                  value={resetData.pass}
                  onChange={(e) => setResetData((s) => ({ ...s, pass: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Confirmar contraseña</label>
                <input
                  type="password"
                  value={resetData.confirm}
                  onChange={(e) => setResetData((s) => ({ ...s, confirm: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn btn-light w-1/3" onClick={() => setMode("login")}>Cancelar</button>
                <button type="submit" className="btn btn-brand w-2/3">Aceptar</button>
              </div>
            </form>
          </Section>
        )}

        {mode === "signup" && (
          <Section title="Crear usuario" onClose={onClose}>
            <form className="space-y-3" onSubmit={handleSignup}>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Nombre completo</label>
                  <input
                    type="text"
                    value={signupData.nombre}
                    onChange={(e) => setSignupData((s) => ({ ...s, nombre: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Empresa</label>
                  <input
                    type="text"
                    value={signupData.empresa}
                    onChange={(e) => setSignupData((s) => ({ ...s, empresa: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">WhatsApp (8 dígitos)</label>
                  <input
                    type="tel"
                    value={signupData.telefono}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(-8);
                      setSignupData((s) => ({ ...s, telefono: v }));
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="55667788"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Dirección</label>
                  <input
                    type="text"
                    value={signupData.direccion}
                    onChange={(e) => setSignupData((s) => ({ ...s, direccion: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Correo</label>
                  <input
                    type="email"
                    value={signupData.correo}
                    autoComplete="email"
                    onChange={(e) => setSignupData((s) => ({ ...s, correo: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Contraseña</label>
                  <input
                    type="password"
                    value={signupData.pass}
                    autoComplete="new-password"
                    onChange={(e) => setSignupData((s) => ({ ...s, pass: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn btn-light w-1/3" onClick={() => setMode("login")}>Volver</button>
                <button type="submit" className="btn btn-brand w-2/3" disabled={loading}>
                  {loading ? "Enviando…" : "Crear cuenta"}
                </button>
              </div>
            </form>
          </Section>
        )}
      </div>
    </div>
  );

  return createPortal(modalUi, document.body);
}
